import type { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import { useId, useRef } from 'react';
import {
  applyInputMask,
  INPUT_MASK_MAX_LENGTH,
  INPUT_MASK_PLACEHOLDER,
  typeMasked,
  type InputMask,
} from '@evogard/domain';
import { cn } from './cn.js';
import { useRequiredMark } from './Form.js';
import { Icon, type IconName } from './Icon.js';

/*
 * `prefix` sai dos atributos nativos de propósito. O do HTML é o `prefix` da
 * RDFa — um mapa de namespaces, tipado como `string` —, e nada nesta casa o
 * escreve num `<input>`. Mantê-lo custaria renomear a prop para uma palavra pior
 * ou aceitar que só texto simples possa ser prefixo.
 */
export interface FieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'prefix'
> {
  label: string;
  error?: string | null;
  help?: ReactNode;
  /** Decorativo, dentro do campo. O rótulo continua sendo quem nomeia. */
  icon?: IconName;
  /**
   * O bloco COLADO à esquerda do campo — o `+55` do telefone, o `R$` do valor.
   *
   * Ele é parte do dado e não decoração: quem lê "11 98888-7777" num campo sem
   * prefixo não sabe se o país já está lá. Por isso ele tem moldura própria,
   * fundo próprio e divide a borda com o campo, em vez de virar um ícone dentro
   * dele.
   *
   * Não é um controle: é texto. Um seletor de país aqui dentro seria um
   * `<select>` de 40px de largura colado num campo, e aí a peça é outra.
   */
  prefix?: ReactNode;
  /**
   * O que fica DENTRO do campo, à direita: a lupa do CEP, a unidade, o estado
   * da consulta.
   *
   * ## Por que ele não é um botão
   *
   * O desenho de 18/09 chama isto de `action_icon`, e a leitura literal seria um
   * botão dentro do campo. Ela não passa: o campo tem 36px, o alvo de toque da
   * WCAG 2.5.5 é 44px, e a régua desta casa (`touch.test.ts`) não abre exceção
   * para "é pequeno mas cabe". Um botão de 28px dentro de um campo é um alvo que
   * o dedo erra, e errar aqui significa apagar o que se acabou de digitar.
   *
   * O gesto que o desenho pede — "procure este CEP" — não precisa de botão: ele
   * acontece no `onBlur` ou no `Enter`, que é quando a pessoa terminou de
   * digitar. O sufixo então DIZ o que está acontecendo (lupa parada, giro
   * enquanto busca) em vez de pedir um clique a mais.
   */
  suffix?: ReactNode;
  /**
   * A confirmação: o que foi validado, em uma frase.
   *
   * Desenha o símbolo verde dentro do campo **e** a frase abaixo dele. Nunca só
   * o símbolo — é a mesma regra do erro, pelo mesmo motivo: cor sozinha não é
   * estado, é decoração, e quem não distingue verde de cinza fica sem a
   * informação inteira.
   *
   * A borda **não** muda de cor, ao contrário do erro. O desenho de 18/09 é
   * explícito nisso (`valid_input.border_color` é a borda normal), e ele está
   * certo: um formulário de nove campos com nove bordas verdes é um formulário
   * gritando, e aí o vermelho do décimo não se destaca de nada.
   *
   * Com `error` junto, o erro vence — um campo não está certo e errado.
   */
  valid?: ReactNode;
  /**
   * CPF, CNPJ, "CPF ou CNPJ" (`document`) ou telefone: o campo pontua enquanto
   * a pessoa digita — `077187` vira `077.187`, `8599` vira `(85) 99`.
   *
   * O `onChange` recebe o evento com o valor JÁ mascarado, e o `value` que
   * chega de fora (o documento do dono preenchido só com dígitos) é mostrado
   * mascarado também. Traz junto o teclado, o tamanho máximo e o exemplo do
   * campo vazio (`077.187.530-42`, `(85) 99999-8888`) — um `placeholder`
   * explícito vence.
   *
   * Máscara é apresentação: quem grava só dígitos é o contrato, e quem diz se o
   * número vale é `isValidCpf`/`normalizePhone`. Ver `input-mask.ts`.
   */
  mask?: InputMask;
  /**
   * O bloco COLADO à direita do campo — o espelho do `prefix`, e este sim é um
   * controle: o botão que abre o calendário do `DateField`.
   *
   * Ele mora FORA da caixa do campo, e é isso que o separa do `suffix`. Um
   * botão dentro de um campo de 36px não chega aos 44px do dedo (ver o
   * `suffix`); colado ao lado, com moldura própria, ele sobe junto com o
   * campo em ponteiro grosso e divide a borda com ele. Quem passa o bloco é
   * responsável pela borda e pelo `-ml-px` dele.
   */
  trailing?: ReactNode;
  /**
   * O rótulo existe, mas só para quem lê a tela com leitor.
   *
   * Para quando o nome visível é de um GRUPO — o `DateTimeField` escreve
   * "Data do ocorrido" uma vez, no `<legend>`, e os dois campos dentro dele
   * se chamam "Data" e "Hora" só para o leitor. Nunca para economizar
   * espaço num campo sozinho: placeholder não é rótulo.
   */
  labelHidden?: boolean;
  /**
   * O campo de 42px, com o prefixo acompanhando — o da calculadora de
   * planejamento da Carteira (20/10/2026), onde o campo é o assunto da tela e
   * não um filtro. Sem ele, um `className` de altura subia o campo e deixava o
   * `R$` com 36px ao lado.
   */
  large?: boolean;
}

/**
 * Rótulo sempre visível — placeholder como rótulo some quando a pessoa mais
 * precisa dele. O erro é texto ligado ao campo por `aria-describedby`, com
 * símbolo junto: borda vermelha sozinha não é estado, é decoração.
 *
 * ## 36px, e não 40 — o campo alcançando o botão
 *
 * As alturas da casa são as do desenho de 05/09, e o `Button` desceu para 36px
 * (`h-9`) naquele dia. O campo ficou em 40 e **ninguém mediu os dois juntos**:
 * numa barra em que "Filtrar" divide a fileira com um `Field`, o botão e o campo
 * erravam a linha de base por 4px desde então. O desenho de 18/09 pede 36px, que
 * é o número que o botão já tinha.
 *
 * Onde há dedo, ele sobe para os 44px da WCAG 2.5.5, como o botão. Num telefone
 * não existe campo apertado.
 */
export function Field({
  label,
  error,
  help,
  icon,
  prefix,
  suffix,
  valid,
  trailing,
  labelHidden,
  large = false,
  className,
  id,
  required,
  mask,
  ...props
}: FieldProps) {
  const generated = useId();
  const masked = useMask(mask, props);
  const idField = id ?? generated;
  const idError = `${idField}-erro`;
  const idHelp = `${idField}-ajuda`;
  const idValid = `${idField}-valido`;

  // Quem explica o `*` é o rodapé do formulário, e ele só o explica se algum
  // campo o desenhou. Fora de um `<Form>` isto não faz nada.
  useRequiredMark(required);

  // `cn` é o merge de classe do Tailwind. Passar identificador de DOM por ele
  // funcionava por sorte — twMerge lê cada pedaço como classe e tem toda a
  // liberdade de reordenar ou descartar o que não reconhece.
  const descricao = [
    help ? idHelp : null,
    error ? idError : null,
    !error && valid ? idValid : null,
  ]
    .filter(Boolean)
    .join(' ');

  /* O erro vence o válido: um campo não está certo e errado ao mesmo tempo. */
  const confirmado = !error && Boolean(valid);
  const inside = Boolean(suffix) || confirmado;

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={idField}
        className={cn('text-xs font-medium text-texto', labelHidden && 'sr-only')}
      >
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </label>

      <div className="flex">
        {prefix && (
          /*
           * `aria-hidden`: o prefixo é lido pelo rótulo do campo ("Telefone com
           * WhatsApp"), e anunciá-lo à parte daria "mais cinquenta e cinco"
           * solto antes do nome do campo.
           */
          <span
            aria-hidden="true"
            className={cn(
              'flex h-9 shrink-0 items-center justify-center rounded-l-borda border border-borda-forte bg-superficie-suave px-2.5 text-xs text-texto-suave pointer-coarse:h-11',
              large && 'h-10.5 rounded-l-[0.625rem] px-3 text-[0.8125rem] font-medium',
            )}
          >
            {prefix}
          </span>
        )}

        <div className="relative min-w-0 flex-1">
          {icon && (
            <Icon
              nome={icon}
              size={16}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-texto-suave"
            />
          )}
          <input
            {...props}
            {...masked}
            id={idField}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={descricao || undefined}
            className={cn(
              'h-9 w-full rounded-borda border bg-superficie px-3 text-xs pointer-coarse:h-11',
              large && 'h-10.5 rounded-[0.625rem] px-3.5 text-sm font-medium',
              'text-texto placeholder:text-texto-tenue',
              'transition-[border-color,background-color]',
              'disabled:cursor-not-allowed disabled:bg-superficie-suave disabled:opacity-60',
              icon && 'pl-9',
              inside && 'pr-9',
              /* `-ml-px` para as duas bordas virarem uma só, e não duas coladas */
              prefix && '-ml-px rounded-l-none',
              trailing && 'rounded-r-none',
              error ? 'border-erro' : 'border-borda-forte hover:border-texto-suave',
              className,
            )}
          />
          {inside && (
            <span className="pointer-events-none absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center text-xs text-texto-suave">
              {confirmado ? (
                <Icon nome="confirmado" size={16} className="text-sucesso" />
              ) : (
                suffix
              )}
            </span>
          )}
        </div>

        {trailing}
      </div>

      {help && (
        <p id={idHelp} className="text-rotulo text-texto-suave">
          {help}
        </p>
      )}
      {error && (
        <p id={idError} className="flex items-center gap-1 text-xs font-medium text-erro">
          <Icon nome="atencao" size={16} />
          {error}
        </p>
      )}
      {confirmado && (
        <p
          id={idValid}
          className="flex items-center gap-1 text-rotulo font-medium text-sucesso"
        >
          <Icon nome="confirmado" size={16} />
          {valid}
        </p>
      )}
    </div>
  );
}

/**
 * O que `mask` acrescenta ao `<input>`: o valor mostrado mascarado, o
 * `onChange` que mascara a tecla e devolve o cursor ao lugar, e os padrões.
 *
 * O texto de antes da tecla fica num `ref`, porque o evento só traz o de
 * depois — e é comparando os dois que se sabe que a pessoa apagou só um
 * separador (ver `typeMasked`).
 */
function useMask(
  mask: InputMask | undefined,
  props: InputHTMLAttributes<HTMLInputElement>,
): InputHTMLAttributes<HTMLInputElement> {
  const shown =
    mask && typeof props.value === 'string'
      ? applyInputMask(mask, props.value)
      : undefined;
  const previous = useRef(
    shown ??
      (mask && typeof props.defaultValue === 'string'
        ? applyInputMask(mask, props.defaultValue)
        : ''),
  );
  if (!mask) return {};
  if (shown !== undefined) previous.current = shown;

  const { onChange } = props;
  return {
    inputMode: props.inputMode ?? (mask === 'phone' ? 'tel' : 'numeric'),
    maxLength: props.maxLength ?? INPUT_MASK_MAX_LENGTH[mask],
    placeholder: props.placeholder ?? INPUT_MASK_PLACEHOLDER[mask],
    ...(shown !== undefined ? { value: shown } : {}),
    ...(typeof props.defaultValue === 'string'
      ? { defaultValue: applyInputMask(mask, props.defaultValue) }
      : {}),
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const next = typeMasked(mask, {
        value: input.value,
        caret: input.selectionStart ?? input.value.length,
        previous: previous.current,
        inputType: (e.nativeEvent as InputEvent).inputType,
      });
      input.value = next.value;
      previous.current = next.value;
      /* Atribuir `value` manda o cursor para o fim; ele volta para depois do dígito. */
      if (document.activeElement === input)
        input.setSelectionRange(next.caret, next.caret);
      onChange?.(e);
    },
  };
}
